import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { ThemeProvider, createTheme, ITheme } from "@fluentui/react";
import { CoherenceTheme, CoherenceV9DarkTheme } from "@coherence-design-system/styles";
import { getUserPreference, saveUserPreference } from "../utils/OPPApiHelper";

export type ThemeMode = "system" | "light" | "dark" | "high-contrast" | "teams-dark" | "midnight-blue" | "warm-gray" | "sand" | "sepia" | "lavender-mist" | "rose" | "sunrise";

/** Concrete theme modes (everything except "system") */
type ConcreteThemeMode = Exclude<ThemeMode, "system">;

/** Resolve "system" to a concrete mode based on OS preference */
function resolveSystemTheme(): ConcreteThemeMode {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Per-theme color configuration */
interface ThemeColors {
  bg: string;
  fg: string;
  readonlyBg: string;
  readonlyBorder: string;
  footerBg: string;
  footerBorder: string;
  isDark: boolean;
}

const themeColorMap: Record<ConcreteThemeMode, ThemeColors> = {
  light: {
    bg: "#faf9f8", fg: "#323130",
    readonlyBg: "#e5e1e1", readonlyBorder: "#ccc",
    footerBg: "white", footerBorder: "#e1e1e1",
    isDark: false,
  },
  dark: {
    bg: "#141414", fg: "#ffffff",
    readonlyBg: "#2d2d2d", readonlyBorder: "#555",
    footerBg: "#212121", footerBorder: "#3d3d3d",
    isDark: true,
  },
  "high-contrast": {
    bg: "#000000", fg: "#ffffff",
    readonlyBg: "#1a1a1a", readonlyBorder: "#ffffff",
    footerBg: "#000000", footerBorder: "#ffffff",
    isDark: true,
  },
  "teams-dark": {
    bg: "#1b1a19", fg: "#d6d6d6",
    readonlyBg: "#2d2c2c", readonlyBorder: "#484644",
    footerBg: "#252423", footerBorder: "#3b3a39",
    isDark: true,
  },
  "midnight-blue": {
    bg: "#0a1929", fg: "#b2bac2",
    readonlyBg: "#0d2137", readonlyBorder: "#1e4976",
    footerBg: "#071320", footerBorder: "#1e4976",
    isDark: true,
  },
  "warm-gray": {
    bg: "#f5f0eb", fg: "#3b3535",
    readonlyBg: "#e8e0d8", readonlyBorder: "#c4b8ad",
    footerBg: "#efe9e3", footerBorder: "#d6cdc4",
    isDark: false,
  },
  sand: {
    bg: "#f8f4e9", fg: "#3a3633",
    readonlyBg: "#ece6d5", readonlyBorder: "#c8bea8",
    footerBg: "#f2ede2", footerBorder: "#d8d0bf",
    isDark: false,
  },
  sepia: {
    bg: "#fbf0d9", fg: "#3d3222",
    readonlyBg: "#f0e2c0", readonlyBorder: "#c9b48a",
    footerBg: "#f5e8cc", footerBorder: "#d6c49e",
    isDark: false,
  },
  "lavender-mist": {
    bg: "#f5f0f7", fg: "#3b3040",
    readonlyBg: "#e8dff0", readonlyBorder: "#c4b5d4",
    footerBg: "#efe8f3", footerBorder: "#d6cbe2",
    isDark: false,
  },
  rose: {
    bg: "#faf0f0", fg: "#3b2e2e",
    readonlyBg: "#f0e0e0", readonlyBorder: "#d4b5b5",
    footerBg: "#f5e8e8", footerBorder: "#e0cccc",
    isDark: false,
  },
  sunrise: {
    bg: "#fff7ed", fg: "#3a3028",
    readonlyBg: "#f5e8d4", readonlyBorder: "#d4b88a",
    footerBg: "#faf0e0", footerBorder: "#e0cca8",
    isDark: false,
  },
};

export function getThemeColors(mode: ThemeMode): ThemeColors {
  const effective = mode === "system" ? resolveSystemTheme() : mode;
  return themeColorMap[effective];
}

/** Build a Fluent UI theme for the custom color modes.
 *  We extend from Coherence base themes to preserve M365 extension slots
 *  (navBackground, stepCompleted, etc.) that Coherence Nav and other components require. */
function getFluentTheme(mode: ThemeMode): ITheme {
  switch (mode) {
    case "dark":
      return CoherenceV9DarkTheme;
    case "high-contrast":
      return createTheme({
        ...CoherenceV9DarkTheme,
        palette: {
          ...CoherenceV9DarkTheme.palette,
          themePrimary: "#1aebff",
          themeDarkAlt: "#3eedff",
          themeDark: "#6bf1ff",
          themeLight: "#b5f8ff",
          neutralPrimary: "#ffffff",
          neutralDark: "#ffffff",
          neutralLighter: "#1a1a1a",
          neutralLight: "#2b2b2b",
          neutralQuaternaryAlt: "#3b3b3b",
          white: "#000000",
          black: "#ffffff",
        },
        semanticColors: {
          ...CoherenceV9DarkTheme.semanticColors,
          bodyBackground: "#000000",
          bodyText: "#ffffff",
          navBackground: "#1a1a1a",
        } as any,
      });
    case "teams-dark":
      return createTheme({
        ...CoherenceV9DarkTheme,
        palette: {
          ...CoherenceV9DarkTheme.palette,
          themePrimary: "#6264a7",
          themeDarkAlt: "#7b7dbe",
          themeDark: "#9294cc",
          themeLight: "#c7c8e6",
          neutralPrimary: "#d6d6d6",
          neutralDark: "#e8e8e8",
          neutralLighter: "#252423",
          neutralLight: "#3b3a39",
          neutralQuaternaryAlt: "#484644",
          white: "#1b1a19",
          black: "#d6d6d6",
        },
        semanticColors: {
          ...CoherenceV9DarkTheme.semanticColors,
          bodyBackground: "#1b1a19",
          bodyText: "#d6d6d6",
          navBackground: "#252423",
        } as any,
      });
    case "midnight-blue":
      return createTheme({
        ...CoherenceV9DarkTheme,
        palette: {
          ...CoherenceV9DarkTheme.palette,
          themePrimary: "#5090d3",
          themeDarkAlt: "#6ba3dc",
          themeDark: "#8ab8e5",
          themeLight: "#c5dbf2",
          neutralPrimary: "#b2bac2",
          neutralDark: "#c8cfd6",
          neutralLighter: "#0d2137",
          neutralLight: "#132f4c",
          neutralQuaternaryAlt: "#1e4976",
          white: "#0a1929",
          black: "#b2bac2",
        },
        semanticColors: {
          ...CoherenceV9DarkTheme.semanticColors,
          bodyBackground: "#0a1929",
          bodyText: "#b2bac2",
          navBackground: "#0d2137",
        } as any,
      });
    case "warm-gray":
      return createTheme({
        ...CoherenceTheme,
        palette: {
          ...CoherenceTheme.palette,
          themePrimary: "#8b6e4e",
          themeDarkAlt: "#7a603f",
          themeDark: "#6a5336",
          themeLight: "#c4a882",
          neutralPrimary: "#3b3535",
          neutralDark: "#2b2525",
          neutralLighter: "#f5f0eb",
          neutralLight: "#e8e0d8",
          neutralQuaternaryAlt: "#d6cdc4",
          white: "#f5f0eb",
          black: "#3b3535",
        },
        semanticColors: {
          ...CoherenceTheme.semanticColors,
          bodyBackground: "#f5f0eb",
          bodyText: "#3b3535",
          navBackground: "#e8e0d8",
        } as any,
      });
    case "sand":
      return createTheme({
        ...CoherenceTheme,
        palette: {
          ...CoherenceTheme.palette,
          themePrimary: "#a0845c",
          themeDarkAlt: "#8d7350",
          themeDark: "#7a6344",
          themeLight: "#c9b48f",
          neutralPrimary: "#3a3633",
          neutralDark: "#2a2623",
          neutralLighter: "#f8f4e9",
          neutralLight: "#ece6d5",
          neutralQuaternaryAlt: "#d8d0bf",
          white: "#f8f4e9",
          black: "#3a3633",
        },
        semanticColors: {
          ...CoherenceTheme.semanticColors,
          bodyBackground: "#f8f4e9",
          bodyText: "#3a3633",
          navBackground: "#ece6d5",
        } as any,
      });
    case "sepia":
      return createTheme({
        ...CoherenceTheme,
        palette: {
          ...CoherenceTheme.palette,
          themePrimary: "#9c6b30",
          themeDarkAlt: "#885d28",
          themeDark: "#744f22",
          themeLight: "#c9a56e",
          neutralPrimary: "#3d3222",
          neutralDark: "#2d2418",
          neutralLighter: "#fbf0d9",
          neutralLight: "#f0e2c0",
          neutralQuaternaryAlt: "#d6c49e",
          white: "#fbf0d9",
          black: "#3d3222",
        },
        semanticColors: {
          ...CoherenceTheme.semanticColors,
          bodyBackground: "#fbf0d9",
          bodyText: "#3d3222",
          navBackground: "#f0e2c0",
        } as any,
      });
    case "lavender-mist":
      return createTheme({
        ...CoherenceTheme,
        palette: {
          ...CoherenceTheme.palette,
          themePrimary: "#7b5ea7",
          themeDarkAlt: "#6c5195",
          themeDark: "#5d4582",
          themeLight: "#b09fcc",
          neutralPrimary: "#3b3040",
          neutralDark: "#2b2030",
          neutralLighter: "#f5f0f7",
          neutralLight: "#e8dff0",
          neutralQuaternaryAlt: "#d6cbe2",
          white: "#f5f0f7",
          black: "#3b3040",
        },
        semanticColors: {
          ...CoherenceTheme.semanticColors,
          bodyBackground: "#f5f0f7",
          bodyText: "#3b3040",
          navBackground: "#e8dff0",
        } as any,
      });
    case "rose":
      return createTheme({
        ...CoherenceTheme,
        palette: {
          ...CoherenceTheme.palette,
          themePrimary: "#c45b5b",
          themeDarkAlt: "#b04e4e",
          themeDark: "#984343",
          themeLight: "#dfa0a0",
          neutralPrimary: "#3b2e2e",
          neutralDark: "#2b1e1e",
          neutralLighter: "#faf0f0",
          neutralLight: "#f0e0e0",
          neutralQuaternaryAlt: "#e0cccc",
          white: "#faf0f0",
          black: "#3b2e2e",
        },
        semanticColors: {
          ...CoherenceTheme.semanticColors,
          bodyBackground: "#faf0f0",
          bodyText: "#3b2e2e",
          navBackground: "#f0e0e0",
        } as any,
      });
    case "sunrise":
      return createTheme({
        ...CoherenceTheme,
        palette: {
          ...CoherenceTheme.palette,
          themePrimary: "#d97706",
          themeDarkAlt: "#c26a05",
          themeDark: "#a85c04",
          themeLight: "#f0b95a",
          neutralPrimary: "#3a3028",
          neutralDark: "#2a201a",
          neutralLighter: "#fff7ed",
          neutralLight: "#f5e8d4",
          neutralQuaternaryAlt: "#e0cca8",
          white: "#fff7ed",
          black: "#3a3028",
        },
        semanticColors: {
          ...CoherenceTheme.semanticColors,
          bodyBackground: "#fff7ed",
          bodyText: "#3a3028",
          navBackground: "#f5e8d4",
        } as any,
      });
    case "light":
    default:
      return { ...CoherenceTheme, neutralHeaderEnabled: true } as any;
  }
}

interface ThemeContextValue {
  themeMode: ThemeMode;
  /** The effective (resolved) theme — same as themeMode unless themeMode is "system" */
  resolvedThemeMode: ConcreteThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  themeMode: "system",
  resolvedThemeMode: "light",
  setThemeMode: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>("system");
  const [themeLoaded, setThemeLoaded] = useState(false);
  // Track the OS-level preference so we re-render when it changes
  const [systemPrefersDark, setSystemPrefersDark] = useState(() =>
    window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false
  );

  // Listen for OS theme changes
  useEffect(() => {
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Resolve "system" to a concrete theme
  const resolvedThemeMode: ConcreteThemeMode = useMemo(() => {
    if (themeMode === "system") return systemPrefersDark ? "dark" : "light";
    return themeMode;
  }, [themeMode, systemPrefersDark]);

  const handleSetThemeMode = useCallback((mode: ThemeMode) => {
    setThemeMode(mode);
    // Persist to DB (fire-and-forget, non-blocking)
    saveUserPreference("themeMode", mode);
  }, []);

  // On mount, load saved theme from DB; default to "system" if nothing saved
  useEffect(() => {
    getUserPreference("themeMode")
      .then((saved) => {
        if (saved) {
          setThemeMode(saved as ThemeMode);
        }
        // else keep "system" default
      })
      .finally(() => setThemeLoaded(true));
  }, []);

  const theme = useMemo(() => getFluentTheme(resolvedThemeMode), [resolvedThemeMode]);

  useEffect(() => {
    const colors = getThemeColors(resolvedThemeMode);
    document.body.style.backgroundColor = colors.bg;
    document.body.style.color = colors.fg;
    document.documentElement.style.setProperty("--readonlyBg", colors.readonlyBg);
    document.documentElement.style.setProperty("--readonlyBorder", colors.readonlyBorder);
    // Toggle charm-ux CUI theme classes so CuiDialog and other CUI components respect dark mode
    document.documentElement.classList.toggle("cui-dark", colors.isDark);
    document.documentElement.classList.toggle("cui-light", !colors.isDark);
  }, [resolvedThemeMode]);

  const value = useMemo(
    () => ({ themeMode, resolvedThemeMode, setThemeMode: handleSetThemeMode }),
    [themeMode, resolvedThemeMode, handleSetThemeMode]
  );

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {themeLoaded ? children : null}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
